<template>
  <div class="container">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h2 class="mb-1">Vorlagen</h2>
        <nav aria-label="breadcrumb">
          <ol class="breadcrumb">
            <li class="breadcrumb-item">
              <router-link :to="{ name: 'teams-list' }" class="text-decoration-none">
                Bereiche
              </router-link>
            </li>
            <li class="breadcrumb-item">
              <router-link :to="{ name: 'teams-detail', params: { id: $route.params.id } }" class="text-decoration-none">
                {{ selectedTeam?.name }}
              </router-link>
            </li>
            <li class="breadcrumb-item">
              <router-link :to="{ name: 'teams-customization-templates', params: { id: $route.params.id } }" class="text-decoration-none">
                Designs
              </router-link>
            </li>
            <li class="breadcrumb-item active" aria-current="page">Vorlagen</li>
          </ol>
        </nav>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-secondary" @click="goBack">
          <font-awesome-icon icon="arrow-left" /> Zurück
        </button>
        <button 
          v-if="isBasketballTemplate" 
          class="btn btn-warning" 
          @click="openBasketballApiModal"
          :disabled="!selectedTeamTemplateId"
        >
          <font-awesome-icon icon="basketball-ball" /> API konfigurieren
        </button>
        <button class="btn btn-primary" @click="openCreateDraftModal" :disabled="!selectedTeamTemplateId">
          <font-awesome-icon icon="plus" /> Neue Vorlage
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Laden...</span>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      {{ error }}
    </div>

    <!-- Content -->
    <div v-else-if="selectedTeam">
      <!-- Design Selection -->
      <div class="card mb-4">
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-6">
              <label for="templateSelect" class="form-label">Design auswählen</label>
              <select 
                id="templateSelect"
                v-model="selectedTeamTemplateId"
                class="form-select"
                @change="onTemplateChange"
              >
                <option value="">Design auswählen...</option>
                <option 
                  v-for="teamTemplate in availableTeamTemplates" 
                  :key="teamTemplate.id"
                  :value="teamTemplate.id"
                >
                  {{ getTemplateDisplayName(teamTemplate) }}
                  <span v-if="teamTemplate.isDefault"> (Standard)</span>
                </option>
              </select>
            </div>
            <div class="col-md-6 d-flex align-items-end">
              <div class="text-muted small">
                <font-awesome-icon icon="info-circle" class="me-1" />
                Wähle ein Design aus, um dessen Vorlagen zu verwalten
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Drafts Table -->
      <TeamsTemplateDraftsTableView
        ref="draftsTableView"
        :drafts="drafts"
        :team-template-fields="teamTemplateFields"
        :selected-team-template-id="selectedTeamTemplateId"
        :pagination="pagination"
        :filters="filters"
        :sort-column="sortColumn"
        :sort-direction="sortDirection"
        :active-filters-count="activeFiltersCount"
        :search-query="searchQuery"
        @execute-api="executeApiCall"
        @edit-draft="editDraft"
        @delete-draft="deleteDraft"
        @create-draft="openCreateDraftModal"
        @change-page="changePage"
        @sort-change="handleSortChange"
        @filter-change="handleFilterChange"
        @clear-filters="handleClearFilters"
        @delete-multiple-drafts="deleteMultipleDrafts"
        @select-all-drafts="selectAllDrafts"
        @search-change="handleSearchChange"
      />
    </div>
    
    <!-- Team not found -->
    <div v-else class="alert alert-gradient-warning" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      Team nicht gefunden.
    </div>
  </div>

  <!-- Create/Edit Draft Modal -->
  <CreateEditDraftModal
    :show-modal="showCreateEditModal"
    :draft="editingDraft"
    :team-template-id="selectedTeamTemplateId"
    :team-template="selectedTeamTemplate"
    @draft-created="onDraftCreated"
    @draft-updated="onDraftUpdated"
    @modal-closed="onModalClosed"
  />

  <!-- Basketball API Config Modal -->
  <BasketballApiConfigModal
    :show-modal="showBasketballApiModal"
    :team-template-id="selectedTeamTemplateId"
    :team-template="selectedTeamTemplate"
    @modal-closed="onBasketballApiModalClosed"
    @settings-saved="onBasketballApiSettingsSaved"
  />


</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useTeamsStore } from '@/stores/teams'
import teamTemplateDraftsService from '@/api/teamTemplateDrafts'
import { templateService } from '@/api/templates'
import { showSuccess, showError, showConfirmDialog } from '@/services/notification'
import CreateEditDraftModal from '@/components/teams/CreateEditDraftModal.vue'
import BasketballApiConfigModal from '@/components/teams/BasketballApiConfigModal.vue'
import TeamsTemplateDraftsTableView from './TeamsTemplateDraftsTableView.vue'

const router = useRouter()
const route = useRoute()
const teamsStore = useTeamsStore()

const { selectedTeam, loading, error } = storeToRefs(teamsStore)


const drafts = ref([])
const searchQuery = ref('')
const availableTeamTemplates = ref([])
const draftsTableView = ref(null)
const pagination = ref({
  currentPage: 1,
  totalPages: 0,
  totalItems: 0,
  itemsPerPage: 10
})


const filters = ref({
  type: '',
  dateFrom: '',
  dateTo: '',
  fields: {}
})

const sortColumn = ref('createdAt')
const sortDirection = ref('desc')


const showCreateEditModal = ref(false)
const editingDraft = ref(null)
const showBasketballApiModal = ref(false)


const selectedTeamTemplateId = ref(route.query.teamTemplateId || null)


const teamTemplateFields = computed(() => {
  if (!selectedTeamTemplateId.value) return []
  
  const selectedTemplate = availableTeamTemplates.value.find(
    template => template.id === selectedTeamTemplateId.value
  )
  
  if (!selectedTemplate?.template?.fields) return []
  
  return selectedTemplate.template.fields.filter(field => field.type !== 'file')
})


const dynamicFilters = computed(() => {
  const filters = {}
  
  teamTemplateFields.value.forEach(field => {
    if (field.type === 'select' && field.options && field.options.choices) {
      filters[field.name] = {
        type: 'select',
        label: field.displayName,
        options: field.options.choices.map(choice => ({
          value: choice.value || choice,
          label: choice.label || choice
        }))
      }
    } else if (field.type === 'checkbox') {
      filters[field.name] = {
        type: 'checkbox',
        label: field.displayName
      }
    } else if (field.type === 'date') {
      filters[field.name] = {
        type: 'date',
        label: field.displayName
      }
    }
  })
  
  return filters
})

const selectedTeamTemplate = computed(() => {
  return availableTeamTemplates.value.find(template => template.id === selectedTeamTemplateId.value)
})

const isBasketballTemplate = computed(() => {
  const templateName = selectedTeamTemplate.value?.template?.name
  return templateName === 'basketball' || templateName?.toLowerCase().includes('basketball')
})


const activeFiltersCount = computed(() => {
  let count = 0
  if (filters.value.type) count++
  if (filters.value.dateFrom) count++
  if (filters.value.dateTo) count++
  count += Object.values(filters.value.fields).filter(v => v && v !== '').length
  return count
})


const handleSortChange = ({ column, direction }) => {
  sortColumn.value = column
  sortDirection.value = direction
  loadDrafts()
}


const handleFilterChange = (newFilters) => {
  filters.value = { ...newFilters }
  pagination.value.currentPage = 1
  loadDrafts()
}


const handleClearFilters = () => {
  filters.value = {
    type: '',
    dateFrom: '',
    dateTo: '',
    fields: {}
  }
  pagination.value.currentPage = 1
  loadDrafts()
}

const handleSearchChange = (query) => {
  searchQuery.value = query
  pagination.value.currentPage = 1
  loadDrafts()
}

const goBack = () => {
  router.push({ name: 'teams-customization-templates', params: { id: route.params.id } })
}

const loadAvailableTeamTemplates = async () => {
  try {
    const response = await templateService.getTeamTemplates(route.params.id)
    availableTeamTemplates.value = response.data || []
    
    
    if (!selectedTeamTemplateId.value && availableTeamTemplates.value.length > 0) {
      selectedTeamTemplateId.value = availableTeamTemplates.value[0].id
    }
    
    
    if (route.query.teamTemplateId) {
      selectedTeamTemplateId.value = route.query.teamTemplateId
    }
  } catch (err) {
    console.error('Error loading available team designs:', err)
  }
}

const getTemplateDisplayName = (teamTemplate) => {
  return teamTemplate.customName || teamTemplate.template.displayName
}

const onTemplateChange = async () => {
  
  await loadDrafts()
}

const loadDrafts = async () => {
  if (!selectedTeamTemplateId.value) {
    drafts.value = []
    pagination.value = {
      currentPage: 1,
      totalPages: 0,
      totalItems: 0,
      itemsPerPage: 10
    }
    return
  }
  
  loading.value = true
  error.value = null
  
  try {
    const params = {
      page: pagination.value.currentPage,
      limit: pagination.value.itemsPerPage,
      search: searchQuery.value
    }
    
    
    if (filters.value.type) {
      params.type = filters.value.type
    }
    
    if (filters.value.dateFrom) {
      params.dateFrom = filters.value.dateFrom
    }
    
    if (filters.value.dateTo) {
      params.dateTo = filters.value.dateTo
    }
    
    
    Object.entries(filters.value.fields).forEach(([fieldName, filterValue]) => {
      if (filterValue && filterValue !== '') {
        params[fieldName] = filterValue
      }
    })
    
    
    if (sortColumn.value && sortDirection.value) {
      params.sortBy = sortColumn.value
      params.sortOrder = sortDirection.value
    }
    
    const response = await teamTemplateDraftsService.getTeamTemplateDrafts(selectedTeamTemplateId.value, params)
    
    drafts.value = response.data.data
    pagination.value = response.data.pagination
  } catch (err) {
    console.error('Error loading drafts:', err)
    error.value = err.response?.data?.error || 'Fehler beim Laden der Vorlagen'
  } finally {
    loading.value = false
  }
}

const debouncedSearch = () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    pagination.value.currentPage = 1
    loadDrafts()
  }, 300)
}

let searchTimeout = null

const changePage = (page) => {
  if (page >= 1 && page <= pagination.value.totalPages) {
    pagination.value.currentPage = page
    loadDrafts()
  }
}

const getVisiblePages = () => {
  const pages = []
  const current = pagination.value.currentPage
  const total = pagination.value.totalPages
  
  let start = Math.max(1, current - 2)
  let end = Math.min(total, current + 2)
  
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  
  return pages
}

const openCreateDraftModal = () => {
  editingDraft.value = null
  showCreateEditModal.value = true
}

const openBasketballApiModal = () => {
  showBasketballApiModal.value = true
}

const editDraft = (draft) => {
  draft.type = 'manual'
  editingDraft.value = draft

  showCreateEditModal.value = true
}

const deleteDraft = async (draft) => {
  const confirmed = await showConfirmDialog(
    'Vorlage löschen',
    `Möchtest du die Vorlage "${draft.data?.name || 'Unbenannte Vorlage'}" wirklich löschen?`,
    'Löschen',
    'Abbrechen',
    {
      icon: 'warning',
      confirmButtonColor: '#dc3545'
    }
  )
  
  if (!confirmed.isConfirmed) return
  
  try {
    await teamTemplateDraftsService.deleteTeamTemplateDraft(draft.id)
    showSuccess('Vorlage erfolgreich gelöscht')
    loadDrafts()
  } catch (err) {
    console.error('Error deleting draft:', err)
    showError(err.response?.data?.error || 'Fehler beim Löschen der Vorlage')
  }
}

const deleteMultipleDrafts = async (draftIds) => {
  const confirmed = await showConfirmDialog(
    'Vorlagen löschen',
    `Möchtest du wirklich ${draftIds.length} Vorlage(n) löschen?`,
    'Löschen',
    'Abbrechen',
    {
      icon: 'warning',
      confirmButtonColor: '#dc3545'
    }
  )
  
  if (!confirmed.isConfirmed) return
  
  try {
    
    const deletePromises = draftIds.map(id => teamTemplateDraftsService.deleteTeamTemplateDraft(id))
    await Promise.all(deletePromises)
    
    showSuccess(`${draftIds.length} Vorlage(n) erfolgreich gelöscht`)
    loadDrafts()
  } catch (err) {
    console.error('Error deleting multiple drafts:', err)
    showError(err.response?.data?.error || 'Fehler beim Löschen der Vorlagen')
  }
}

const selectAllDrafts = async () => {
  try {
    
    const params = {
      limit: 10000, 
      search: searchQuery.value
    }
    
    
    if (filters.value.type) {
      params.type = filters.value.type
    }
    
    if (filters.value.dateFrom) {
      params.dateFrom = filters.value.dateFrom
    }
    
    if (filters.value.dateTo) {
      params.dateTo = filters.value.dateTo
    }
    
    
    Object.entries(filters.value.fields).forEach(([fieldName, filterValue]) => {
      if (filterValue && filterValue !== '') {
        params[fieldName] = filterValue
      }
    })
    
    const response = await teamTemplateDraftsService.getTeamTemplateDrafts(selectedTeamTemplateId.value, params)
    const allDrafts = response.data.data
    
    
    if (draftsTableView.value) {
      draftsTableView.value.setAllDraftIds(allDrafts.map(draft => draft.id))
    }
  } catch (err) {
    console.error('Error loading all drafts for selection:', err)
    showError('Fehler beim Laden aller Vorlagen')
  }
}



const executeApiCall = async (draft) => {
  try {
    await teamTemplateDraftsService.executeApiCall(draft.id)
    showSuccess('API-Aufruf erfolgreich durchgeführt')
    loadDrafts()
  } catch (err) {
    console.error('Error executing API call:', err)
    showError(err.response?.data?.error || 'Fehler beim API-Aufruf')
  }
}

const downloadExcelTemplate = async (draft) => {
  try {
    const response = await teamTemplateDraftsService.downloadExcelTemplate(draft.id)
    
    
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `${draft.data?.name || 'template'}_template.xlsx`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
    
    showSuccess('Excel-Template erfolgreich heruntergeladen')
  } catch (err) {
    console.error('Error downloading excel template:', err)
    showError(err.response?.data?.error || 'Fehler beim Herunterladen der Excel-Vorlage')
  }
}



const onDraftCreated = () => {
  showCreateEditModal.value = false
  showSuccess('Vorlage erfolgreich erstellt')
  loadDrafts()
}

const onDraftUpdated = () => {
  showCreateEditModal.value = false
  showSuccess('Vorlage erfolgreich aktualisiert')
  loadDrafts()
}

const onModalClosed = () => {
  showCreateEditModal.value = false
  loadDrafts()
}

const onBasketballApiModalClosed = () => {
  showBasketballApiModal.value = false
}

const onBasketballApiSettingsSaved = () => {
  showSuccess('Basketball API-Einstellungen wurden aktualisiert')
}




const getTypeBadgeClass = (type) => {
  const classes = {
    api: 'bg-primary',
    excel: 'bg-success',
    manual: 'bg-secondary'
  }
  return classes[type] || 'bg-secondary'
}

const getTypeIcon = (type) => {
  const icons = {
    api: 'code',
    excel: 'file-excel',
    manual: 'edit'
  }
  return icons[type] || 'file'
}

const getTypeLabel = (type) => {
  const labels = {
    api: 'API',
    excel: 'Excel',
    manual: 'Manuell'
  }
  return labels[type] || type
}

const getStatusBadgeClass = (status) => {
  const classes = {
    draft: 'bg-secondary',
    active: 'bg-success',
    inactive: 'bg-warning text-dark',
    processing: 'bg-info'
  }
  return classes[status] || 'bg-secondary'
}

const getStatusLabel = (status) => {
  const labels = {
    draft: 'Entwurf',
    active: 'Aktiv',
    inactive: 'Inaktiv',
    processing: 'Verarbeitung'
  }
  return labels[status] || status
}

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('de-DE')
}

const getFieldIcon = (fieldType) => {
  const icons = {
    text: 'font',
    number: 'hashtag',
    date: 'calendar',
    select: 'list',
    checkbox: 'check-square',
    textarea: 'align-left',
    money: 'euro-sign',
    location: 'map-marker-alt'
  }
  return icons[fieldType] || 'tag'
}

const formatFieldValue = (value, field) => {
  if (!value) return '-'
  
  
  switch (field.type) {
    case 'date':
      return new Date(value).toLocaleDateString('de-DE')
    case 'checkbox':
      return value ? 'Ja' : 'Nein'
    case 'number':
    case 'money':
      return typeof value === 'number' ? value.toLocaleString('de-DE') : value
    case 'select':
      
      if (field.options?.choices) {
        const choice = field.options.choices.find(c => 
          (c.value || c) === value
        )
        return choice ? (choice.label || choice) : value
      }
      return value
    default:
      return value
  }
}


onMounted(async () => {
  if (!selectedTeam.value || selectedTeam.value.id !== route.params.id) {
    await teamsStore.loadTeamById(route.params.id)
  }
  
  await loadAvailableTeamTemplates()
  await loadDrafts()
})


watch(() => route.query.teamTemplateId, async (newTeamTemplateId) => {
  selectedTeamTemplateId.value = newTeamTemplateId
  await loadDrafts()
})
</script>
